import {
  Medication,
  MedicationRequest,
  MedicationStatement,
  Patient,
  ValueSetExpansionContains,
} from '@medplum/fhirtypes'
import { ChartItem } from './PatientChart.utils.tsx'
import { Badge, Box, Select, TextInput } from '@mantine/core'
import { formatDateTime } from '@/utils.ts'
import { ResourceTable } from '@/pages/Patient/components/ResourceTable.tsx'
import { JSX, useState } from 'react'
import { ValueSetAutocomplete } from '@medplum/react'
import { AddResourceSection } from './AddResourceSection.tsx'
import { useDoseSpotPrescriptions } from '@/hooks/useDoseSpotPrescriptions'
import {
  doseSpotDispenseLabel,
  doseSpotDrugCodeLabel,
  doseSpotMedicationText,
  doseSpotPrescriberLabel,
  isDoseSpotPrescription,
} from './doseSpotPrescription'
import { DoseSpotStatusBadge } from './DoseSpotStatusBadge'

type Props = {
  patient: Patient
  item: ChartItem
}

type Combined = MedicationStatement & { _medication: Medication }

/**
 * The two kinds of row this section shows. They **coexist, badged, with no auto-dedup**
 * (§11.7): the note stays authoritative for reported/intended medications, DoseSpot is
 * authoritative for prescriptions, and a false merge between them would be worse than a
 * visible duplicate. A drug both prescribed in DoseSpot and typed into a note's Plan
 * therefore shows twice, and that is correct behaviour.
 */
type MedicationRow = MedicationStatement | MedicationRequest

const RXNORM_MEDS_VALUE_SET = 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113762.1.4.1010.4'

const STATUS_OPTIONS: { value: NonNullable<MedicationStatement['status']>; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'entered-in-error', label: 'Entered in error' },
  { value: 'intended', label: 'Intended' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'not-taken', label: 'Not Taken' },
]

const availFields = [
  { code: 'medication', title: 'Medication', width: '16%' },
  { code: 'dose', title: 'Dose', width: '8%' },
  { code: 'route', title: 'Route', width: '6%' },
  { code: 'frequency', title: 'Frequency', width: '11%' },
  { code: 'status', title: 'Status', width: '7%' },
  { code: 'effective', title: 'Started', width: '9%' },
  { code: 'stopped', title: 'Stopped', width: '9%' },
  { code: 'indication', title: 'Indication', width: '10%' },
  { code: 'practitioner', title: 'Practitioner', width: '12%' },
  { code: 'note', title: 'Note', width: '12%' },
]

/**
 * Pull a leading "value unit" dose (e.g. "300 mg", "2 puffs") out of the
 * combined dosage narrative ("300 mg PO once daily"). Fallback for the Dose
 * column on legacy/manually-added meds that stored the dose only in
 * `dosage[0].text` and never populated structured `doseAndRate.doseQuantity`.
 */
const parseDoseFromText = (text?: string): string => {
  if (!text) return ''
  const match = text.match(/^\d+(?:\.\d+)?\s*[A-Za-z%µ/]+/)
  return match ? match[0].trim() : ''
}

/**
 * A DoseSpot-synced prescription, rendered **read-only** into the same columns.
 *
 * The status column is DoseSpot's own two-axis vocabulary verbatim rather than the
 * derived FHIR `status`, because FHIR cannot say `Error` and that is precisely what a
 * prescriber needs to see (§10.6). Route comes structured off the sync's cached drug
 * lookup (`dosageInstruction[0].route`). Frequency stays empty: DoseSpot sends the rest
 * of the sig as one free-text string and there is no sig parsing.
 */
const renderDoseSpotColumn = (
  prescription: MedicationRequest,
  itemCode: string,
): string | JSX.Element => {
  switch (itemCode) {
    case 'medication': {
      const codeLabel = doseSpotDrugCodeLabel(prescription)
      return (
        <div className="flex flex-col items-start gap-1">
          <span>{doseSpotMedicationText(prescription) || '-'}</span>
          <div className="flex items-center gap-1">
            <Badge size="xs" variant="light" color="blue">
              DoseSpot
            </Badge>
            {codeLabel && <span className="text-xs text-[#7D7987]">{codeLabel}</span>}
          </div>
        </div>
      )
    }
    case 'dose':
      // The sig is one string; the leading "1 tablet"-shaped fragment is all that can be
      // read out of it without guessing, and an unreadable one leaves the cell empty.
      return parseDoseFromText(prescription.dosageInstruction?.[0]?.text)
    case 'route':
      return prescription.dosageInstruction?.[0]?.route?.text || ''
    case 'status':
      return <DoseSpotStatusBadge prescription={prescription} />
    case 'effective':
      return prescription.authoredOn ? formatDateTime(prescription.authoredOn) : ''
    case 'practitioner':
      return doseSpotPrescriberLabel(prescription)
    case 'note': {
      const directions = prescription.dosageInstruction?.[0]?.text
      const lines = [directions, doseSpotDispenseLabel(prescription)].filter(Boolean)
      if (lines.length === 0) return ''
      return (
        <div>
          {lines.map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
      )
    }
    // 'frequency' — DoseSpot has no structured timing, only the sig text. 'stopped' —
    // DoseSpot's stop date is not on the prescription. 'indication' — no field to carry one.
    default:
      return ''
  }
}

const renderColumn = (resourceSource: MedicationRow, itemCode: string): string | JSX.Element => {
  if (isDoseSpotPrescription(resourceSource)) {
    return renderDoseSpotColumn(resourceSource, itemCode)
  }

  const resource = resourceSource as Combined

  switch (itemCode) {
    case 'medication':
      return (
        resource._medication?.code?.text ||
        resource._medication?.code?.coding?.[0].display ||
        resource.medicationCodeableConcept?.text ||
        resource.medicationCodeableConcept?.coding?.[0]?.display ||
        resource.medicationCodeableConcept?.coding?.[0]?.code ||
        resource.contained?.find((item) => item.resourceType === 'Medication')?.code?.text ||
        '-'
      )
    case 'dose': {
      // Prefer the structured `doseAndRate.doseQuantity` (stamped by the
      // cascade in `middleware/src/visit/visit.utils.ts:toMedicationStatement`
      // for new meds). Fall back to the non-standard `dosage[0].doseQuantity`
      // slot some legacy resources use, then to extracting the leading dose
      // from the combined `dosage[0].text` for older meds written before the
      // structured field existed.
      const dose = resource.dosage?.[0]?.doseAndRate?.[0]?.doseQuantity
      if (dose?.value !== undefined) return `${dose.value}${dose.unit ? ` ${dose.unit}` : ''}`

      // @ts-expect-error legacy non-standard slot
      const dose2 = resource.dosage?.[0]?.doseQuantity
      if (dose2?.value !== undefined) return `${dose2.value}${dose2.unit ? ` ${dose2.unit}` : ''}`

      return parseDoseFromText(resource.dosage?.[0]?.text)
    }
    case 'route':
      return (
        resource.dosage?.[0]?.route?.text || resource.dosage?.[0]?.route?.coding?.[0]?.display || ''
      )
    case 'frequency':
      return (
        resource.dosage?.[0]?.timing?.code?.text ||
        resource.dosage?.[0]?.timing?.code?.coding?.[0]?.display ||
        ''
      )
    case 'status':
      return resource.status || '-'
    case 'effective': {
      if (resource.effectiveDateTime) return formatDateTime(resource.effectiveDateTime)
      if (resource.effectivePeriod?.start) return formatDateTime(resource.effectivePeriod.start)
      if (resource.dateAsserted) return formatDateTime(resource.dateAsserted)
      return ''
    }
    case 'stopped':
      return resource.effectivePeriod?.end ? formatDateTime(resource.effectivePeriod.end) : ''
    case 'indication':
      return (
        <div>
          {(resource.reasonCode || []).map((r, idx) => (
            <div key={idx}>{r.text || r.coding?.[0]?.display || '-'}</div>
          ))}
        </div>
      )
    case 'practitioner':
      return resource.informationSource?.display || ''
    case 'note': {
      // The doctor's free-text note + any older `additionalInstruction` slot.
      // Dose/route/frequency now have dedicated columns, so this column holds
      // only the related free-text context (e.g. "Initiated for tumor lysis
      // prophylaxis"), not the dosage narrative.
      const additional = resource.dosage?.[0]?.additionalInstruction?.[0]?.text
      const note = resource.note?.[0]?.text
      const lines = [note, additional].filter(Boolean)
      if (lines.length === 0) return ''
      return (
        <div>
          {lines.map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
      )
    }
    default:
      return ''
  }
}

const preprocessList = (list: (MedicationRow | Medication)[]): MedicationRow[] => {
  const medications = list.filter((item) => item.resourceType === 'Medication')
  const statements = list.filter((item) => item.resourceType === 'MedicationStatement')

  const resourceList: MedicationStatement[] = []
  for (const stmt of statements) {
    const medication = stmt.medicationReference?.reference
    if (medication) {
      const medId = medication.split('/').pop()
      const medRes = medications.find((m) => m.id === medId)
      if (medRes) {
        ;(stmt as Combined)._medication = medRes
      }
    }

    // No date-based dedup. Distinct medications — including the patient's
    // existing history — legitimately share the same `effectiveDateTime` when
    // they're materialized from the same progress note (the cascade stamps one
    // note-date on every med). Collapsing by date therefore hid real meds: the
    // nav badge counted all N MedicationStatements while the table rendered
    // only the first. Render every statement as its own row.
    resourceList.push(stmt)
  }

  return resourceList
}

export const Medications = ({ patient, item }: Props) => {
  // Read separately from the table's own `MedicationStatement` search, so a DoseSpot
  // failure can never empty the note-authored list beside it.
  const doseSpotPrescriptions = useDoseSpotPrescriptions(patient.id)
  const [picked, setPicked] = useState<{ system?: string; code: string; display: string } | null>(
    null,
  )
  const [pickerKey, setPickerKey] = useState(0)
  const [status, setStatus] = useState<string | null>('active')
  const [dose, setDose] = useState('')
  const [instructions, setInstructions] = useState('')

  const resetForm = () => {
    setPicked(null)
    setStatus('active')
    setDose('')
    setInstructions('')
    setPickerKey((k) => k + 1)
  }

  const handlePickerChange = (items: ValueSetExpansionContains[]) => {
    const first = items[0]
    if (!first?.code) {
      setPicked(null)
      return
    }
    setPicked({
      system: first.system || 'http://www.nlm.nih.gov/research/umls/rxnorm',
      code: first.code,
      display: first.display || first.code,
    })
  }

  const buildResource = (): MedicationStatement | { error: string } => {
    if (!picked?.code) return { error: 'Please select a medication.' }
    const doseStr = dose.trim()
    // Parse a leading "value + single unit token" (e.g. "300 mg") into a
    // structured doseQuantity so the Dose column lights up. Capture ONLY the
    // first unit token — route/frequency words ("10 mg twice daily") belong in
    // dosage text, not in doseQuantity.unit, which must be a single (UCUM) unit.
    // The full instruction is preserved verbatim in dosage[0].text below. This
    // mirrors the cascade's `parseDoseQuantity` in `middleware/src/visit/visit.utils.ts`.
    const doseMatch = doseStr.match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z%/]+)?/)
    const doseValue = doseMatch ? Number(doseMatch[1]) : NaN
    const doseQuantity =
      doseMatch && !Number.isNaN(doseValue)
        ? { value: doseValue, ...(doseMatch[2]?.trim() ? { unit: doseMatch[2].trim() } : {}) }
        : undefined
    return {
      resourceType: 'MedicationStatement',
      status: (status || 'active') as NonNullable<MedicationStatement['status']>,
      subject: { reference: `Patient/${patient.id}` },
      medicationCodeableConcept: {
        coding: [
          {
            system: picked.system,
            code: picked.code,
            display: picked.display,
          },
        ],
        text: picked.display,
      },
      effectiveDateTime: new Date().toISOString(),
      dateAsserted: new Date().toISOString(),
      dosage: doseStr
        ? [
            {
              text: doseStr,
              ...(doseQuantity ? { doseAndRate: [{ doseQuantity }] } : {}),
              additionalInstruction: instructions.trim()
                ? [{ text: instructions.trim() }]
                : undefined,
            },
          ]
        : undefined,
      note: instructions.trim() && !doseStr ? [{ text: instructions.trim() }] : undefined,
    }
  }

  return (
    <AddResourceSection<MedicationStatement>
      patient={patient}
      title="Medications"
      modalTitle="Add Medication"
      resourceType="MedicationStatement"
      featureContext="MedicationStatement"
      noun="medication"
      isSubmitDisabled={!picked?.code}
      resetForm={resetForm}
      buildResource={buildResource}
      renderForm={() => (
        <>
          <Box>
            <p className="mb-1 text-sm font-medium">Medication (RxNorm) *</p>
            <ValueSetAutocomplete
              key={pickerKey}
              name="add-medication"
              placeholder="Search RxNorm medication"
              binding={RXNORM_MEDS_VALUE_SET}
              maxValues={1}
              clearable={false}
              onChange={handlePickerChange}
            />
            {picked && (
              <p className="mt-1 text-xs text-gray-500">
                Selected: {picked.display} ({picked.code})
              </p>
            )}
          </Box>
          <TextInput
            label="Dose"
            placeholder="e.g. 10 mg twice daily"
            value={dose}
            onChange={(e) => setDose(e.currentTarget.value)}
          />
          <TextInput
            label="Instructions"
            placeholder="e.g. Take with food"
            value={instructions}
            onChange={(e) => setInstructions(e.currentTarget.value)}
          />
          <Select label="Status" data={STATUS_OPTIONS} value={status} onChange={setStatus} />
        </>
      )}
    >
      {(refreshKey) => (
        <ResourceTable<MedicationRow>
          key={refreshKey}
          patient={patient}
          item={item}
          availFields={availFields}
          renderColumn={renderColumn}
          defaultSort="effective"
          blockRemove
          preprocessList={preprocessList}
          extraResources={doseSpotPrescriptions}
        />
      )}
    </AddResourceSection>
  )
}
